import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { publicApi } from '../lib/api';
import { usePageContent } from '../hooks/usePageContent';

const SHARED = [
  'Professional camera and lighting', 'Glam-style photographs',
  'Unlimited digital captures during booth operation', 'Personalised photo overlay',
  'Personalised attract screen', 'Classic backdrop', 'Digital sharing',
  'Private online gallery', 'Booth attendant', 'Setup and pack-down',
];

const MAYDEN_MESSAGES = [
  { name: 'Video files only', price: 'add $150', points: ['Individual guest recordings', 'Organised digital video files', 'No completed highlight film'] },
  { name: 'Cinematic film', price: 'add $300', points: ['Guest recordings', 'Professionally edited 3–5 minute cinematic film', 'Selected messages & suitable slow-motion footage', 'Opening title & licensed music', 'Audio cleanup & cinematic colour grading', 'Full HD 1080p delivery', 'One consolidated revision'] },
];

const GlamBooth = () => {
  const { t } = usePageContent('glam_booth');
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    publicApi.getPackages().then((d) => setTiers((d || []).filter((p) => p.category === 'glambooth').sort((a, b) => a.order - b.order))).catch(() => {});
  }, []);

  const heroImage = t('hero_image', '');

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo exactTitle="Glam Booth Hire Cranbourne | Mayden Films" description="The Mayden Films Glam Booth blends professional photography, flattering lighting and interactive fun. Packages from $399 across Cranbourne and Southeast Melbourne." path="/glam-booth" />

      <section className="relative pt-36 pb-16 bg-gradient-to-br from-[#1F1A17] via-[#2A2420] to-[#1F1A17] overflow-hidden">
        {heroImage && (
          <div className="absolute inset-0">
            <img src={heroImage} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#1F1A17]/80 via-[#1F1A17]/85 to-[#1F1A17]" />
          </div>
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C5A880] tracking-[0.25em] uppercase text-xs font-semibold mb-4">{t('hero_eyebrow', 'The Glam Booth')}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] mb-5">{t('hero_title', 'An unforgettable guest experience')}</h1>
          <p className="text-base md:text-lg text-[#EADBC8]/70 max-w-2xl mx-auto">
            {t('hero_subtitle', 'The Mayden Films Glam Booth combines professional photography, flattering lighting and interactive entertainment to create an unforgettable experience for your guests.')}
          </p>
        </div>
      </section>

      {/* Shared inclusions */}
      <section className="py-12 bg-[#1A1612]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-[#C5A880] text-sm uppercase tracking-widest font-semibold mb-6">{t('shared_title', 'Every Glam Booth package includes')}</h2>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl mx-auto">
            {SHARED.map((f) => (
              <li key={f} className="flex items-start text-[#EADBC8]/80 text-sm"><Check size={16} className="text-[#C5A880] mr-2 mt-0.5 flex-shrink-0" />{f}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Tier cards */}
      <section className="py-14 bg-[#1F1A17]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {tiers.map((t) => (
              <Card key={t.id} className={`bg-[#2A2420] flex flex-col ${t.popular ? 'border-2 border-[#C5A880] shadow-xl shadow-[#C5A880]/10' : 'border-[#C5A880]/20'}`} data-testid={`glam-tier-${t.name.toLowerCase()}`}>
                <CardContent className="p-6 flex flex-col flex-1">
                  {t.badge && (
                    <span className="self-start inline-flex items-center gap-1 bg-[#C5A880] text-[#1F1A17] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                      <Star size={10} fill="currentColor" /> {t.badge}
                    </span>
                  )}
                  <h3 className="text-xl font-bold text-[#EADBC8]">{t.name}</h3>
                  <p className="text-3xl font-bold text-[#C5A880] mt-1 mb-1">{t.price}</p>
                  <p className="text-[#EADBC8]/50 text-xs mb-4">{t.duration} of booth operation</p>
                  <ul className="space-y-1.5 mb-6 flex-1">
                    {(t.features || []).map((f) => (
                      <li key={f} className={`flex items-start text-sm ${/not included/i.test(f) ? 'text-[#EADBC8]/40' : 'text-[#EADBC8]/80'}`}>
                        <Check size={14} className="text-[#C5A880] mr-2 mt-0.5 flex-shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/quote"><Button className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold" data-testid={`glam-quote-${t.name.toLowerCase()}`}>Request {t.name} Package</Button></Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-[#EADBC8]/45 text-xs mt-6">All prices are "from" prices in AUD. Availability is confirmed individually; a 30% booking fee and signed agreement secure your date.</p>
        </div>
      </section>

      {/* Mayden Messages */}
      <section className="py-16 bg-[#1A1612]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-[#C5A880] text-sm uppercase tracking-widest font-semibold mb-3"><Sparkles size={16} /> Mayden Messages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-4">{t('mm_title', 'A cinematic video guestbook')}</h2>
            <p className="text-[#EADBC8]/70">
              {t('mm_subtitle', 'Mayden Messages captures the voices, personalities and heartfelt words of your guests — not just their photographs. Guests are guided through short recorded messages, supplied individually or transformed into a professionally edited cinematic keepsake.')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {MAYDEN_MESSAGES.map((m) => (
              <Card key={m.name} className="bg-[#2A2420] border-[#C5A880]/20">
                <CardContent className="p-6">
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="text-lg font-bold text-[#EADBC8]">{m.name}</h3>
                    <span className="text-xl font-bold text-[#C5A880]">{m.price}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {m.points.map((p) => (
                      <li key={p} className="flex items-start text-[#EADBC8]/75 text-sm"><Check size={14} className="text-[#C5A880] mr-2 mt-0.5 flex-shrink-0" />{p}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-[#EADBC8]/55 text-sm mt-6">Mayden Messages is a Glam Booth addition (not a standalone booking). It is already included in the <span className="text-[#C5A880] font-medium">Ultimate</span> Glam Booth package at $1,099.</p>
          <div className="text-center mt-6">
            <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-5" data-testid="mm-enquire-btn">Add Mayden Messages to a Glam Booth</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-[#2A2420] via-[#1F1A17] to-[#2A2420]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#EADBC8] mb-5">{t('cta_title', 'Ready to book your Glam Booth?')}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-10 py-6 text-base">Request a Quote</Button></Link>
            <Link to="/previous-events?category=glambooth"><Button variant="outline" className="border-2 border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880] hover:text-[#1F1A17] font-semibold px-10 py-6 text-base" data-testid="glam-see-recent-events">View Our Work</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlamBooth;
